<template>
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
        <!-- Ausgewählter Bereich (deleteRegion): alles andere wird abgedunkelt -->
        <template v-if="region">
            <div class="vz-dim absolute inset-0" :style="dimStyle" />
            <div class="vz-region absolute" :style="regionStyle" />
        </template>

        <!-- Nachzeichnen: ein Lichtband tastet das Bild ab, der Rand "läuft" -->
        <template v-else-if="effect === 'trace'">
            <div class="vz-trace-border absolute inset-0" />
            <div class="vz-sweep vz-sweep--vertical absolute inset-y-0" />
        </template>

        <!-- Farbanalyse: horizontales Band, Farbchips pulsieren -->
        <template v-else-if="effect === 'scan'">
            <div class="vz-sweep vz-sweep--horizontal absolute inset-x-0" />
        </template>

        <!-- Vergleich: ein Wischer fährt über das Bild -->
        <template v-else-if="effect === 'compare'">
            <div class="vz-wipe absolute inset-y-0" />
        </template>

        <!-- Formen: Zielmarken in den Ecken -->
        <template v-else-if="effect === 'target'">
            <div class="vz-corner vz-corner--tl" />
            <div class="vz-corner vz-corner--tr" />
            <div class="vz-corner vz-corner--bl" />
            <div class="vz-corner vz-corner--br" />
        </template>

        <!-- Drehen: das Bild selbst kippt kurz an -->
        <div
            v-else-if="effect === 'rotate'"
            class="vz-rotate absolute inset-0"
        />

        <!-- Farben, mit denen gerade gearbeitet wird -->
        <div
            v-if="colors.length > 0"
            class="absolute top-2 left-2 flex flex-wrap gap-1"
        >
            <span
                v-for="(color, index) in colors"
                :key="color + String(index)"
                class="vz-chip h-4 w-4 rounded-full border-2 border-white shadow"
                :style="{
                    animationDelay: `${String(index * 120)}ms`,
                    backgroundColor: color,
                }"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

/**
 * Hebt auf der Bühne hervor, woran ein Werkzeug der KI gerade arbeitet:
 * einen Bildbereich, bestimmte Farben oder einfach die Art der Tätigkeit
 * (nachzeichnen, analysieren, vergleichen …).
 */
const props = defineProps<{
    input: Record<string, unknown>;
    tool: string;
}>();

type Effect = "compare" | "none" | "rotate" | "scan" | "target" | "trace";

const EFFECTS: Record<string, Effect> = {
    analyzeColors: "scan",
    buildColorMasks: "scan",
    compareToOriginal: "compare",
    deleteElement: "target",
    deleteRegion: "target",
    deleteShapes: "target",
    drawSvg: "trace",
    inspectShapes: "target",
    isolateRegion: "target",
    mapComplexity: "scan",
    planVectorization: "scan",
    prepareImage: "scan",
    rotateImage: "rotate",
    traceBitmap: "trace",
    traceMasks: "trace",
    traceRegion: "trace",
    upscaleImage: "scan",
};

const effect = computed<Effect>(() => EFFECTS[props.tool] ?? "none");

const region = computed(() => {
    if (props.tool !== "deleteRegion") return;

    const { height, width, x, y } = props.input;

    if (
        typeof x !== "number" ||
        typeof y !== "number" ||
        typeof width !== "number" ||
        typeof height !== "number"
    ) {
        return;
    }

    return { height, width, x, y };
});

const regionStyle = computed(() =>
    region.value
        ? {
              height: `${String(region.value.height * 100)}%`,
              left: `${String(region.value.x * 100)}%`,
              top: `${String(region.value.y * 100)}%`,
              width: `${String(region.value.width * 100)}%`,
          }
        : {},
);

// Der abgedunkelte Rest entsteht aus einem "Loch" per clip-path.
const dimStyle = computed(() => {
    if (!region.value) return {};

    const { height, width, x, y } = region.value;
    const left = String(x * 100);
    const top = String(y * 100);
    const right = String((x + width) * 100);
    const bottom = String((y + height) * 100);

    return {
        clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${left}% ${top}%, ${left}% ${bottom}%, ${right}% ${bottom}%, ${right}% ${top}%, ${left}% ${top}%)`,
    };
});

const colors = computed<string[]>(() => {
    const entries = props.input.colors;

    if (!Array.isArray(entries)) return [];

    return entries
        .map((entry) =>
            typeof entry === "object" && entry !== null && "hex" in entry
                ? String((entry as { hex: unknown }).hex)
                : undefined,
        )
        .filter((hex): hex is string => hex !== undefined)
        .slice(0, 12);
});
</script>

<style scoped>
.vz-dim {
    background: rgb(16 42 67 / 0.55);
    animation: vz-fade-in 300ms ease-out both;
}

.vz-region {
    border: 2px dashed #fff;
    border-radius: 4px;
    box-shadow: 0 0 0 1px rgb(19 135 236 / 0.9);
    animation: vz-region-pulse 1.2s ease-in-out infinite;
}

.vz-trace-border {
    border: 2px dashed rgb(19 135 236 / 0.9);
    border-radius: 6px;
    animation: vz-dash 1.5s linear infinite;
}

.vz-sweep {
    background: linear-gradient(
        var(--vz-sweep-direction, to right),
        transparent,
        rgb(255 255 255 / 0.5) 45%,
        rgb(19 135 236 / 0.9) 50%,
        rgb(255 255 255 / 0.5) 55%,
        transparent
    );
    mix-blend-mode: screen;
    opacity: 0.85;
}

.vz-sweep--vertical {
    --vz-sweep-direction: to right;
    width: 18%;
    animation: vz-sweep-x 1.7s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

.vz-sweep--horizontal {
    --vz-sweep-direction: to bottom;
    height: 22%;
    animation: vz-sweep-y 1.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

.vz-wipe {
    width: 3px;
    background: rgb(19 135 236 / 0.95);
    box-shadow: 0 0 12px rgb(19 135 236 / 0.8);
    animation: vz-wipe 2.2s ease-in-out infinite alternate;
}

.vz-corner {
    position: absolute;
    width: 18%;
    height: 18%;
    border: 3px solid rgb(19 135 236 / 0.95);
    animation: vz-corner-pulse 1.1s ease-in-out infinite;
}

.vz-corner--tl {
    top: 4%;
    left: 4%;
    border-right: 0;
    border-bottom: 0;
    border-top-left-radius: 6px;
}

.vz-corner--tr {
    top: 4%;
    right: 4%;
    border-left: 0;
    border-bottom: 0;
    border-top-right-radius: 6px;
}

.vz-corner--bl {
    bottom: 4%;
    left: 4%;
    border-right: 0;
    border-top: 0;
    border-bottom-left-radius: 6px;
}

.vz-corner--br {
    right: 4%;
    bottom: 4%;
    border-left: 0;
    border-top: 0;
    border-bottom-right-radius: 6px;
}

.vz-rotate {
    border: 2px dashed rgb(19 135 236 / 0.9);
    border-radius: 6px;
    animation: vz-rotate 1.6s ease-in-out infinite alternate;
}

.vz-chip {
    animation: vz-chip-pulse 1.2s ease-in-out infinite;
}

@keyframes vz-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes vz-region-pulse {
    0%,
    100% {
        box-shadow: 0 0 0 1px rgb(19 135 236 / 0.9);
    }

    50% {
        box-shadow: 0 0 0 6px rgb(19 135 236 / 0.35);
    }
}

@keyframes vz-dash {
    to {
        /* dashed border "marching ants" via offset trick */
        transform: translateZ(0);
        opacity: 0.55;
    }
}

@keyframes vz-sweep-x {
    0% {
        left: -20%;
    }

    100% {
        left: 102%;
    }
}

@keyframes vz-sweep-y {
    0% {
        top: -25%;
    }

    100% {
        top: 105%;
    }
}

@keyframes vz-wipe {
    from {
        left: 2%;
    }

    to {
        left: 98%;
    }
}

@keyframes vz-corner-pulse {
    0%,
    100% {
        opacity: 0.35;
        transform: scale(1);
    }

    50% {
        opacity: 1;
        transform: scale(1.08);
    }
}

@keyframes vz-rotate {
    from {
        transform: rotate(-3deg);
    }

    to {
        transform: rotate(3deg);
    }
}

@keyframes vz-chip-pulse {
    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.35);
    }
}
</style>
